import { useEffect, useState } from 'react';
import api from '@/api/axios';
import { toast } from 'sonner';
import {
  Sparkle, Fire, ThermometerSimple, Snowflake, Users, ChartBar,
  CircleNotch, Phone, MapPin, ArrowRight, CheckCircle,
} from '@phosphor-icons/react';

const LEVEL_META = {
  hot: { label: 'CALIENTE', icon: Fire, color: 'red', emoji: '🔥' },
  warm: { label: 'TIBIO', icon: ThermometerSimple, color: 'amber', emoji: '🌡️' },
  cold: { label: 'FRÍO', icon: Snowflake, color: 'slate', emoji: '❄️' },
};

const Prospectos = () => {
  const [tab, setTab] = useState('leads');
  const [leads, setLeads] = useState([]);
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState({});
  const [filters, setFilters] = useState({ days_no_contact: 2, levels: 'hot,warm' });

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        days_no_contact: String(filters.days_no_contact),
        levels: filters.levels,
        limit: '100',
      });
      const res = await api.get(`/prospects/hot-leads/list?${params.toString()}`);
      setLeads(res.data.leads || []);
    } catch (e) {
      toast.error('Error cargando prospectos');
    } finally {
      setLoading(false);
    }
  };

  const fetchSegments = async () => {
    try {
      const res = await api.get('/prospects/segments/list');
      setSegments(res.data.segments || []);
    } catch (e) {
      // noop
    }
  };

  useEffect(() => {
    fetchLeads();
    fetchSegments();
  }, [filters.days_no_contact, filters.levels]);

  const reanalyze = async (customerId) => {
    setAnalyzing((p) => ({ ...p, [customerId]: true }));
    try {
      await api.post(`/prospects/analyze/${customerId}`);
      toast.success('Análisis actualizado');
      fetchLeads();
      fetchSegments();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error analizando');
    } finally {
      setAnalyzing((p) => ({ ...p, [customerId]: false }));
    }
  };

  const totalLeads = leads.length;
  const hotCount = leads.filter((l) => l.prospect_level === 'hot').length;
  const warmCount = leads.filter((l) => l.prospect_level === 'warm').length;

  return (
    <div className="p-6 max-w-[1400px] mx-auto" style={{ background: 'var(--bg)', color: 'var(--text)' }} data-testid="prospects-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--text-faint)] font-bold flex items-center gap-1.5">
            <Sparkle size={11} weight="fill" /> IA · Claude Sonnet 4.5
          </p>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight heading-font">Prospectos</h1>
          <p className="text-xs text-[var(--text-muted)] mt-1">
            Clientes con alta intención de compra detectada por IA.
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <select
            value={filters.levels}
            onChange={(e) => setFilters({ ...filters, levels: e.target.value })}
            className="bg-[var(--surface)] border border-[var(--border)] text-[var(--text)] rounded-md px-3 py-2 text-sm"
            data-testid="filter-levels"
          >
            <option value="hot">Solo calientes (80+)</option>
            <option value="hot,warm">Calientes + tibios</option>
            <option value="hot,warm,cold">Todos</option>
          </select>
          <select
            value={filters.days_no_contact}
            onChange={(e) => setFilters({ ...filters, days_no_contact: Number(e.target.value) })}
            className="bg-[var(--surface)] border border-[var(--border)] text-[var(--text)] rounded-md px-3 py-2 text-sm"
            data-testid="filter-days-no-contact"
          >
            <option value={0}>Sin filtro de contacto</option>
            <option value={1}>≥1 día sin contactar</option>
            <option value={2}>≥2 días sin contactar</option>
            <option value={5}>≥5 días sin contactar</option>
            <option value={7}>≥7 días sin contactar</option>
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-[var(--border)]">
        {[
          { id: 'leads', label: 'Hot Leads', icon: Fire },
          { id: 'segments', label: 'Audiencias (Meta Ads)', icon: ChartBar },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-bold flex items-center gap-2 border-b-2 transition-colors ${
              tab === t.id
                ? 'border-[var(--primary)] text-[var(--text)]'
                : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text)]'
            }`}
            data-testid={`tab-${t.id}`}
          >
            <t.icon size={16} weight="bold" />
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'leads' && (
        <>
          {/* Stat row */}
          <div className="grid grid-cols-3 gap-3 mb-5">
            <StatCard label="Total" value={totalLeads} color="slate" data-testid="stat-total" />
            <StatCard label="🔥 Calientes" value={hotCount} color="red" />
            <StatCard label="🌡️ Tibios" value={warmCount} color="amber" />
          </div>

          {loading ? (
            <div className="text-center py-12 text-[var(--text-muted)] text-sm">
              <CircleNotch size={24} className="animate-spin inline mb-2" />
              <p>Cargando prospectos…</p>
            </div>
          ) : leads.length === 0 ? (
            <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-10 text-center" data-testid="leads-empty">
              <CheckCircle size={28} weight="fill" className="text-emerald-500 inline mb-2" />
              <p className="text-sm font-bold text-[var(--text)]">
                ¡Todos los prospectos están al día!
              </p>
              <p className="text-xs text-[var(--text-muted)] mt-1">
                Cuando entren mensajes nuevos, la IA detectará automáticamente intención de compra.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="leads-grid">
              {leads.map((l) => (
                <LeadCard key={l.id} lead={l} onReanalyze={reanalyze} analyzing={!!analyzing[l.id]} />
              ))}
            </div>
          )}
        </>
      )}

      {tab === 'segments' && (
        <div data-testid="segments-tab">
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-5 mb-4">
            <p className="text-sm text-[var(--text-muted)]">
              <strong className="text-[var(--text)]">Audiencias automáticas</strong> generadas por la IA agrupando los intereses
              de tus clientes. Úsalas para crear campañas de Meta Ads más efectivas (ej: anuncio de &quot;bisagras&quot;
              solo para los clientes que ya mostraron interés en bisagras).
            </p>
          </div>
          {segments.length === 0 ? (
            <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-10 text-center">
              <p className="text-sm text-[var(--text-muted)]">
                Aún no hay segmentos. Analiza algunos clientes con IA para generar audiencias.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="segments-grid">
              {segments.map((s) => (
                <div
                  key={s.category}
                  className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-4 hover:border-[var(--primary)] transition-colors"
                  data-testid={`segment-${s.category}`}
                >
                  <p className="text-[10px] uppercase tracking-wide text-[var(--text-faint)] font-bold">Audiencia</p>
                  <h3 className="text-lg font-black text-[var(--text)] truncate capitalize">{s.category}</h3>
                  <div className="mt-2 flex items-center justify-between text-xs">
                    <span className="text-[var(--text-muted)]"><Users size={12} weight="bold" className="inline mr-1" />{s.customers} clientes</span>
                    <span className="font-bold text-[var(--primary)]">avg {s.avg_score}</span>
                  </div>
                  <div className="mt-2 flex gap-2 text-[10px] font-bold">
                    <span className="px-1.5 py-0.5 rounded bg-red-500/15 text-red-500 border border-red-500/30">🔥 {s.hot}</span>
                    <span className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-600 border border-amber-500/30">🌡️ {s.warm}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, color }) => {
  const map = {
    slate: 'bg-[var(--surface)] border-[var(--border)] text-[var(--text)]',
    red: 'bg-red-500/10 border-red-500/30 text-red-600 dark:text-red-400',
    amber: 'bg-amber-500/10 border-amber-500/30 text-amber-600 dark:text-amber-400',
  };
  return (
    <div className={`border rounded-[14px] p-4 ${map[color] || map.slate}`}>
      <p className="text-[10px] uppercase tracking-wide font-bold opacity-80">{label}</p>
      <p className="text-3xl font-black mt-0.5">{value}</p>
    </div>
  );
};

const LeadCard = ({ lead, onReanalyze, analyzing }) => {
  const meta = LEVEL_META[lead.prospect_level] || LEVEL_META.cold;
  const colorRing = lead.prospect_level === 'hot'
    ? 'border-red-500/40 bg-red-500/5'
    : lead.prospect_level === 'warm'
    ? 'border-amber-500/40 bg-amber-500/5'
    : 'border-[var(--border)] bg-[var(--surface)]';
  return (
    <div className={`border rounded-[14px] p-4 shadow-[var(--shadow-sm)] ${colorRing}`} data-testid={`lead-card-${lead.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <p className="text-base font-black text-[var(--text)] truncate">{lead.name}</p>
          <p className="text-[10px] text-[var(--text-muted)] flex items-center gap-1.5 flex-wrap">
            <span>{lead.platform === 'whatsapp' ? '📱 WhatsApp' : '💬 Messenger'}</span>
            {lead.phone && <><span>·</span><span>{lead.phone}</span></>}
            {lead.days_since_vendor_contact !== null && lead.days_since_vendor_contact !== undefined && (
              <><span>·</span><span className="text-orange-500 font-bold">{lead.days_since_vendor_contact}d sin contacto</span></>
            )}
          </p>
        </div>
        <div className={`shrink-0 px-2.5 py-1 rounded-full border font-black text-xs flex items-center gap-1 ${
          lead.prospect_level === 'hot' ? 'bg-red-500/20 text-red-600 dark:text-red-400 border-red-500/40' :
          lead.prospect_level === 'warm' ? 'bg-amber-500/20 text-amber-600 dark:text-amber-400 border-amber-500/40' :
          'bg-slate-500/20 text-slate-500 border-slate-500/40'
        }`}>
          <span>{meta.emoji}</span>
          <span>{lead.prospect_score}</span>
        </div>
      </div>

      {lead.prospect_action && (
        <div className="bg-[var(--bg)] border border-[var(--border)] rounded-md p-2 mb-2">
          <p className="text-[10px] uppercase tracking-wide text-[var(--text-faint)] font-bold mb-0.5 flex items-center gap-1">
            <ArrowRight size={10} weight="bold" /> Acción sugerida
          </p>
          <p className="text-xs text-[var(--text)] leading-snug">{lead.prospect_action}</p>
        </div>
      )}

      {lead.prospect_categories?.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {lead.prospect_categories.map((c) => (
            <span key={c} className="text-[10px] px-2 py-0.5 bg-[var(--surface-muted)] border border-[var(--border)] rounded-full text-[var(--text-muted)] capitalize">
              {c}
            </span>
          ))}
        </div>
      )}

      {lead.prospect_reasoning && (
        <p className="text-[10px] text-[var(--text-faint)] italic line-clamp-2">{lead.prospect_reasoning}</p>
      )}

      <div className="flex items-center justify-between gap-2 mt-3">
        <button
          onClick={() => onReanalyze(lead.id)}
          disabled={analyzing}
          className="text-[10px] font-bold px-2 py-1 rounded bg-purple-500/15 border border-purple-500/30 text-purple-600 dark:text-purple-400 hover:bg-purple-500/25 disabled:opacity-50 flex items-center gap-1"
          data-testid={`btn-reanalyze-${lead.id}`}
        >
          {analyzing ? <CircleNotch size={11} className="animate-spin" /> : <Sparkle size={11} weight="bold" />}
          Re-analizar
        </button>
        <a
          href={`/inbox?customer=${lead.id}`}
          className="text-[10px] font-bold px-2 py-1 rounded bg-[var(--primary)] text-white hover:bg-[var(--primary-hover)] flex items-center gap-1"
          data-testid={`btn-open-chat-${lead.id}`}
        >
          Abrir chat <ArrowRight size={11} weight="bold" />
        </a>
      </div>
    </div>
  );
};

export default Prospectos;
